import { useEffect, useRef, useState, type CSSProperties } from 'react';
import logo160 from '../assets/brand/kazbia-logo-160.webp';
import logo320 from '../assets/brand/kazbia-logo-320.webp';
import logo480 from '../assets/brand/kazbia-logo-480.webp';
import logo560 from '../assets/brand/kazbia-logo-560.webp';
import logo720 from '../assets/brand/kazbia-logo-720.webp';
import logo960 from '../assets/brand/kazbia-logo-960.webp';
import maskUrl from '../assets/brand/kazbia-mask-640.webp';
import './kazbia-logo.css';

/**
 * The game's logo (owner 24/10: "Kazbia Online", the dragon art in kazbia-site/kazbia-logo),
 * in the places the reference game draws its own: the top bar, the "Entering world" cover, the
 * about page, the login card and the boot splash.
 *
 * Animated variants port the owner's page (kazbia-logo/index.html): zoom/blur entrance, float,
 * a gold shine sweeping the name through the mask, eye / mouth / belly / jewels pulsing, twinkles,
 * embers and mouth fire on canvas, and a lightning bolt with a flash every 7 s. Everything stops
 * with prefers-reduced-motion, and the canvas only runs while the logo is on screen.
 */
export type KazbiaLogoVariant = 'top' | 'card' | 'hero' | 'loading' | 'splash';

const SRCSET = `${logo320} 320w, ${logo480} 480w, ${logo560} 560w, ${logo720} 720w, ${logo960} 960w`;
/** Rendered width of each variant (kazbia-logo.css), so the browser picks the smallest file. */
const SIZES: Record<Exclude<KazbiaLogoVariant, 'top'>, string> = {
  card: '(max-width: 322px) 62vw, 200px',
  hero: '(max-width: 666px) 84vw, 560px',
  loading: '(max-width: 545px) 76vw, 413px',
  splash: '(max-width: 525px) 80vw, 420px',
};
/** Canvas particles (embers, fire, lightning) per variant; the splash is too brief for them. */
const FX: Record<Exclude<KazbiaLogoVariant, 'top'>, boolean> = { card: true, hero: true, loading: true, splash: false };

/** Canvas particles start this long after the logo shows (then on the next idle moment). */
const FX_DELAY_MS = 900;

/** The entrance plays once per page load and variant (switching login ↔ register remounts the card). */
const introPlayed = new Set<KazbiaLogoVariant>();

function reducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

// Glow / twinkle spots on the art, in % of the 2:1 box (owner's page).
const GLOWS: Array<{ kind: string; left: number; top: number; width?: number; delay?: number }> = [
  { kind: 'belly', left: 50.9, top: 41.9 },
  { kind: 'mouth', left: 58.9, top: 38.2 },
  { kind: 'eye', left: 57.7, top: 27.4 },
  { kind: 'gem', left: 13, top: 59.2 },
  { kind: 'gem', left: 90.7, top: 33.7, delay: 0.6 },
  { kind: 'gem', left: 78.9, top: 86.6, delay: 1.2 },
  { kind: 'gem', left: 49.5, top: 73.2, width: 3, delay: 0.3 },
  { kind: 'gem', left: 49.5, top: 84.7, width: 3, delay: 0.9 },
];
const STARS: Array<{ left: number; top: number; width?: number; delay?: number }> = [
  { left: 30.7, top: 79.6 },
  { left: 68.4, top: 79.6, delay: 1.3 },
  { left: 66.2, top: 48.4, width: 3.6, delay: 0.7 },
  { left: 13, top: 59.2, width: 4, delay: 2 },
];

/**
 * Resolves `settleMs` after a file whose URL contains `match` has finished downloading, or after `timeoutMs`.
 * The about page starts the logo after its walking outfits' atlas, so the outfits keep their
 * ~0.9 s at 10 Mbps (owner's goal) instead of sharing the connection with the logo.
 */
export function afterResource(match: string, timeoutMs: number, settleMs = 0): Promise<void> {
  return new Promise((resolve) => {
    let done = false;
    let observer: PerformanceObserver | null = null;
    const finish = () => {
      if (done) return;
      done = true;
      observer?.disconnect();
      resolve();
    };
    window.setTimeout(finish, timeoutMs);
    try {
      // Downloaded, then `settleMs` more for the page to decode and draw it.
      const seen = () => { observer?.disconnect(); window.setTimeout(finish, settleMs); };
      if (performance.getEntriesByType('resource').some((entry) => entry.name.includes(match))) { seen(); return; }
      observer = new PerformanceObserver((list) => {
        if (list.getEntries().some((entry) => entry.name.includes(match))) seen();
      });
      observer.observe({ type: 'resource', buffered: true });
    } catch {
      finish();
    }
  });
}

export function KazbiaLogo({ variant, className = '', after }: { variant: KazbiaLogoVariant; className?: string; after?: Promise<unknown> }) {
  if (variant === 'top') {
    return <img className={`kz-top ${className}`} src={logo160} width={68} height={34} alt="Kazbia Online" decoding="async" draggable={false} />;
  }
  return <AnimatedLogo variant={variant} className={className} after={after} />;
}

function AnimatedLogo({ variant, className, after }: { variant: Exclude<KazbiaLogoVariant, 'top'>; className: string; after?: Promise<unknown> }) {
  const [still] = useState(reducedMotion);
  // The picture downloads only once `after` settles (the about page's first downloads go first).
  const [ready, setReady] = useState(!after);
  useEffect(() => {
    if (!after || ready) return undefined;
    let alive = true;
    void after.finally(() => { if (alive) setReady(true); });
    return () => { alive = false; };
  }, [after, ready]);
  const [intro] = useState(() => {
    const first = !introPlayed.has(variant);
    introPlayed.add(variant);
    return first && !still;
  });
  const [loaded, setLoaded] = useState(false);
  // The shine's mask (23 KB) is fetched only when the sweep is about to start (its first pass is at
  // 2 s), so it does not share the connection with the page's first downloads.
  const [shine, setShine] = useState(false);
  useEffect(() => {
    if (still || !loaded || shine) return undefined;
    const timer = window.setTimeout(() => setShine(true), intro ? 1500 : 600);
    return () => window.clearTimeout(timer);
  }, [still, loaded, shine, intro]);
  const rootRef = useRef<HTMLDivElement>(null);
  const backRef = useRef<HTMLCanvasElement>(null);
  const frontRef = useRef<HTMLCanvasElement>(null);
  const surgeRef = useRef<HTMLDivElement>(null);
  const flashRef = useRef<HTMLDivElement>(null);
  const fx = FX[variant] && !still;

  useEffect(() => {
    if (!fx || !loaded) return undefined;
    const root = rootRef.current;
    const back = backRef.current;
    const front = frontRef.current;
    if (!root || !back || !front) return undefined;
    // The particles wait for an idle moment (at most FX_DELAY_MS): the page's own first work — the
    // about page's walking outfits, the login form — goes first (10 Mbps measurement, 24/10).
    let stop: (() => void) | null = null;
    const w = window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number; cancelIdleCallback?: (id: number) => void };
    let idle = 0;
    const timer = window.setTimeout(() => {
      const go = () => { stop = runEffects(root, back, front, surgeRef.current, flashRef.current, intro ? 1400 : 1200); };
      if (w.requestIdleCallback) idle = w.requestIdleCallback(go, { timeout: 600 });
      else go();
    }, FX_DELAY_MS);
    return () => {
      window.clearTimeout(timer);
      if (idle) w.cancelIdleCallback?.(idle);
      stop?.();
    };
  }, [fx, loaded, intro]);

  const classes = ['kz-logo', `kz-logo--${variant}`, still ? '' : 'kz-anim', intro ? 'kz-intro' : '', loaded ? 'is-loaded' : '', className]
    .filter(Boolean)
    .join(' ');
  return (
    <div ref={rootRef} className={classes} role="img" aria-label="Kazbia Online" style={{ '--kz-mask': `url(${maskUrl})` } as CSSProperties}>
      {!still ? <div className="kz-aura" aria-hidden /> : null}
      {fx ? <canvas ref={backRef} className="kz-fx kz-fx-back" aria-hidden /> : null}
      <div className="kz-intro-box">
        <div className="kz-float">
          <div className="kz-surge" ref={surgeRef}>
            <img
              className="kz-logo-img"
              // No `src`: with it Edge downloaded the 480 px file as well as the srcset pick (10 Mbps
              // measurement, 24/10). Every browser the game runs on reads srcset.
              sizes={SIZES[variant]}
              srcSet={ready ? SRCSET : undefined}
              width={960}
              height={480}
              alt=""
              decoding="async"
              draggable={false}
              onLoad={() => setLoaded(true)}
            />
            {!still && loaded ? (
              <>
                {shine ? <div className="kz-shine" aria-hidden /> : null}
                {GLOWS.map((glow, index) => (
                  <i
                    key={`g${index}`}
                    className={`kz-glow kz-${glow.kind}`}
                    aria-hidden
                    style={{ left: `${glow.left}%`, top: `${glow.top}%`, ...(glow.width ? { width: `${glow.width}%` } : {}), ...(glow.delay ? { animationDelay: `${glow.delay}s` } : {}) }}
                  />
                ))}
                {STARS.map((star, index) => (
                  <i
                    key={`s${index}`}
                    className="kz-star"
                    aria-hidden
                    style={{ left: `${star.left}%`, top: `${star.top}%`, ...(star.width ? { width: `${star.width}%` } : {}), ...(star.delay ? { animationDelay: `${star.delay}s` } : {}) }}
                  />
                ))}
              </>
            ) : null}
          </div>
        </div>
      </div>
      {fx ? <canvas ref={frontRef} className="kz-fx kz-fx-front" aria-hidden /> : null}
      {fx ? <div className="kz-flash" ref={flashRef} aria-hidden /> : null}
    </div>
  );
}

type Ember = { x: number; y: number; vx: number; vy: number; r: number; life: number; max: number; w: number; red: boolean };
type Spark = { x: number; y: number; vx: number; vy: number; r: number; life: number; max: number };
type Bolt = { p: Array<[number, number]>; br: Array<[number, number]>; life: number; max: number };

/**
 * Embers, mouth fire and lightning (owner's canvas loop), drawn on two canvases that extend past
 * the logo box (kazbia-logo.css). Counts scale with the logo width; motion is per 60 Hz frame,
 * scaled by the real frame time. Returns the cleanup.
 */
function runEffects(
  root: HTMLElement,
  back: HTMLCanvasElement,
  front: HTMLCanvasElement,
  surge: HTMLElement | null,
  flash: HTMLElement | null,
  firstBoomMs: number,
): () => void {
  const b = back.getContext('2d');
  const f = front.getContext('2d');
  if (!b || !f) return () => undefined;
  const dpr = Math.min(window.devicePixelRatio || 1, window.innerWidth < 700 ? 1.5 : 2);
  let W = 0;
  let H = 0;
  // Logo box inside the canvas (both canvases share the geometry).
  let box = { left: 0, top: 0, width: 1, height: 1 };
  const size = () => {
    const c = back.getBoundingClientRect();
    const r = root.getBoundingClientRect();
    W = c.width;
    H = c.height;
    box = { left: r.left - c.left, top: r.top - c.top, width: r.width, height: r.height };
    for (const canvas of [back, front]) {
      canvas.width = Math.max(1, Math.round(W * dpr));
      canvas.height = Math.max(1, Math.round(H * dpr));
    }
    b.setTransform(dpr, 0, 0, dpr, 0, 0);
    f.setTransform(dpr, 0, 0, dpr, 0, 0);
  };
  size();
  const scale = Math.max(0.35, Math.min(1, box.width / 900));
  const emberCount = Math.round(85 * scale);
  const fireCount = Math.round(45 * scale);

  const ember = (): Ember => ({
    x: box.left + Math.random() * box.width,
    y: box.top + box.height * (0.6 + Math.random() * 0.5),
    vx: (Math.random() - 0.5) * 0.4,
    vy: -(0.5 + Math.random() * 1.4) * (0.6 + 0.4 * scale),
    r: (0.8 + Math.random() * 2.2) * (0.6 + 0.4 * scale),
    life: 0,
    max: 120 + Math.random() * 180,
    w: Math.random() * 6.28,
    red: Math.random() < 0.4,
  });
  const fire = (): Spark => {
    const mx = box.left + box.width * 0.6;
    const my = box.top + box.height * 0.39;
    const d = Math.random() < 0.5 ? -1 : 1;
    return {
      x: mx + d * box.width * 0.008,
      y: my + (Math.random() - 0.5) * box.height * 0.02,
      vx: d * (0.9 + Math.random() * 1.6) * scale,
      vy: (Math.random() - 0.5) * 0.5,
      r: (1.5 + Math.random() * 3) * scale,
      life: 0,
      max: 28 + Math.random() * 30,
    };
  };
  const bolt = (): Bolt => {
    const side = Math.random() < 0.5;
    const x0 = side ? box.left + box.width * (0.05 + Math.random() * 0.25) : box.left + box.width * (0.7 + Math.random() * 0.25);
    const pts: Array<[number, number]> = [[x0, 0]];
    let x = x0;
    let y = 0;
    const ty = box.top + box.height * (0.25 + Math.random() * 0.2);
    const step = Math.max(0.4, scale);
    while (y < ty) {
      y += (12 + Math.random() * 22) * step;
      x += (Math.random() - 0.5) * 40 * step;
      pts.push([x, y]);
    }
    const br: Array<[number, number]> = [];
    const k = Math.floor(pts.length * 0.4);
    let [bx, by] = pts[k] ?? [x0, 0];
    br.push([bx, by]);
    for (let j = 0; j < 5; j += 1) {
      bx += (side ? -1 : 1) * (10 + Math.random() * 20) * step;
      by += (10 + Math.random() * 18) * step;
      br.push([bx, by]);
    }
    return { p: pts, br, life: 0, max: 22 };
  };
  const embers: Ember[] = [];
  for (let i = 0; i < emberCount; i += 1) {
    const e = ember();
    e.life = Math.random() * e.max;
    embers.push(e);
  }
  const sparks: Spark[] = [];
  let bolts: Bolt[] = [];

  const line = (ctx: CanvasRenderingContext2D, p: Array<[number, number]>, w: number, col: string) => {
    ctx.strokeStyle = col;
    ctx.lineWidth = w;
    ctx.beginPath();
    ctx.moveTo(p[0]![0], p[0]![1]);
    for (let i = 1; i < p.length; i += 1) ctx.lineTo(p[i]![0], p[i]![1]);
    ctx.stroke();
  };
  const dot = (ctx: CanvasRenderingContext2D, x: number, y: number, r: number, a: number, c1: string, c2: string) => {
    if (a <= 0.01 || r <= 0) return;
    const g = ctx.createRadialGradient(x, y, 0, x, y, r);
    g.addColorStop(0, `${c1}${a})`);
    g.addColorStop(0.35, `${c2}${a * 0.7})`);
    g.addColorStop(1, 'rgba(255,40,0,0)');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, 6.283);
    ctx.fill();
  };
  // Embers fade before the canvas top instead of being cut by it.
  const edgeFade = (y: number) => Math.max(0, Math.min(1, y / (H * 0.22)));

  let frame = 0;
  let last = performance.now();
  let running = false;
  const tick = (now: number) => {
    const k = Math.min(3, (now - last) / 16.667);
    last = now;
    b.clearRect(0, 0, W, H);
    f.clearRect(0, 0, W, H);
    b.globalCompositeOperation = 'lighter';
    f.globalCompositeOperation = 'lighter';
    b.lineCap = 'round';
    b.lineJoin = 'round';
    for (const o of bolts) {
      o.life += k;
      const a = 1 - o.life / o.max;
      if (a <= 0) continue;
      const flick = Math.floor(o.life) % 4 < 2 ? 1 : 0.55;
      line(b, o.p, 9 * Math.max(0.5, scale), `rgba(255,90,40,${a * 0.25 * flick})`);
      line(b, o.p, 3 * Math.max(0.5, scale), `rgba(255,190,140,${a * 0.8 * flick})`);
      line(b, o.p, 1.2, `rgba(255,255,240,${a * flick})`);
      line(b, o.br, 1.5, `rgba(255,200,150,${a * 0.7 * flick})`);
    }
    bolts = bolts.filter((o) => o.life < o.max);
    for (let i = 0; i < embers.length; i += 1) {
      const p = embers[i]!;
      p.life += k;
      p.w += 0.05 * k;
      p.x += (p.vx + Math.sin(p.w) * 0.35) * k;
      p.y += p.vy * k;
      if (p.life > p.max) {
        embers[i] = ember();
        continue;
      }
      const t = p.life / p.max;
      const a = (t < 0.15 ? t / 0.15 : 1 - t) * edgeFade(p.y);
      dot(i % 5 === 0 ? f : b, p.x, p.y, p.r * 4, a, p.red ? 'rgba(255,150,120,' : 'rgba(255,225,150,', p.red ? 'rgba(230,40,20,' : 'rgba(255,130,30,');
    }
    if (sparks.length < fireCount) sparks.push(fire());
    for (let i = 0; i < sparks.length; i += 1) {
      const q = sparks[i]!;
      q.life += k;
      q.x += q.vx * k;
      q.y += q.vy * k;
      q.vx *= Math.pow(0.985, k);
      q.vy += (Math.random() - 0.5) * 0.06 * k;
      q.r *= Math.pow(1.02, k);
      if (q.life > q.max) {
        sparks[i] = fire();
        continue;
      }
      const t = q.life / q.max;
      dot(f, q.x, q.y, q.r * 3, (1 - t) * 0.8, t < 0.4 ? 'rgba(255,245,190,' : 'rgba(255,160,60,', 'rgba(255,80,10,');
    }
    frame = requestAnimationFrame(tick);
  };
  const start = () => {
    if (running) return;
    running = true;
    last = performance.now();
    frame = requestAnimationFrame(tick);
  };
  const stop = () => {
    running = false;
    cancelAnimationFrame(frame);
  };

  const boom = () => {
    if (!running) return;
    bolts.push(bolt());
    if (Math.random() < 0.7) bolts.push(bolt());
    for (const [el, cls] of [[surge, 'boom'], [flash, 'on']] as const) {
      if (!el) continue;
      el.classList.remove(cls);
      void el.offsetWidth;
      el.classList.add(cls);
    }
  };
  let interval = 0;
  const first = window.setTimeout(() => {
    boom();
    interval = window.setInterval(boom, 7000);
  }, firstBoomMs);

  // Only while the logo is on screen (about page scrolled down, hidden cover).
  let observer: IntersectionObserver | null = null;
  if (typeof IntersectionObserver !== 'undefined') {
    observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) start();
      else stop();
    });
    observer.observe(back);
  } else {
    start();
  }
  // Layout settling (fonts, the card growing) and window resizes re-measure the canvases.
  let resizeFrame = 0;
  const onResize = () => {
    cancelAnimationFrame(resizeFrame);
    resizeFrame = requestAnimationFrame(size);
  };
  const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(onResize) : null;
  resizeObserver?.observe(root);
  resizeObserver?.observe(back);
  window.addEventListener('resize', onResize);
  return () => {
    stop();
    observer?.disconnect();
    resizeObserver?.disconnect();
    window.clearTimeout(first);
    window.clearInterval(interval);
    cancelAnimationFrame(resizeFrame);
    window.removeEventListener('resize', onResize);
  };
}
